import React from 'react';
import { useFmt } from './tmShared';

/**
 * Campo de valor com máscara de moeda. Digita-se só números e eles entram pelos
 * centavos (padrão de app bancário): 3 → 0,03 · 300000 → 3.000,00. O formato
 * (símbolo, milhar, decimal) segue a moeda e o idioma de quem está vendo.
 * `value` e `onChange` trafegam o número em texto simples ("3000.5") ou "".
 */
const MoneyInput: React.FC<{
  id?: string;
  value: string;
  onChange: (v: string) => void;
  currency: string;
  placeholder?: string;
  className?: string;
  required?: boolean;
  ariaLabel?: string;
  max?: number;
}> = ({ id, value, onChange, currency, placeholder, className, required, ariaLabel, max = 100000000 }) => {
  const { locale } = useFmt();
  const fmt = (n: number) => {
    try { return new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n); } catch { return n.toFixed(2); }
  };
  const n = value === '' || value == null ? null : parseFloat(value);
  const shown = n == null || !Number.isFinite(n) ? '' : fmt(n);

  const handle = (raw: string) => {
    const digits = raw.replace(/\D/g, '').replace(/^0+/, '');
    if (!digits) { onChange(''); return; }
    const cents = Math.min(parseInt(digits.slice(-13), 10), Math.round(max * 100));
    onChange(String(cents / 100));
  };

  return (
    <input
      id={id}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      className={className}
      value={shown}
      placeholder={placeholder ? fmt(parseFloat(placeholder)) : undefined}
      required={required}
      aria-label={ariaLabel}
      onChange={(e) => handle(e.target.value)}
      onKeyDown={(e) => {
        // Backspace com o cursor no fim apaga o último dígito (o símbolo no fim, em alguns idiomas, atrapalharia).
        if (e.key === 'Backspace' && value) {
          e.preventDefault();
          const digits = Math.round(parseFloat(value) * 100).toString().slice(0, -1);
          onChange(digits ? String(parseInt(digits, 10) / 100) : '');
        }
      }}
    />
  );
};

export default MoneyInput;
